import { useParams, Link } from 'react-router-dom';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useBatchJob } from '@/hooks/useBatchJobs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Clock, AlertCircle, CheckCircle2, XCircle, Loader2, Hash } from 'lucide-react';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  indexed: 'default',
  compiled: 'secondary',
  batched: 'secondary',
  ready_for_index: 'secondary',
  queued: 'outline',
  failed: 'destructive',
  index_error: 'destructive',
  ai_error: 'destructive',
  retry_pending: 'outline',
};

function statusIcon(status: string) {
  if (status === 'indexed') return <CheckCircle2 className="h-3 w-3 text-green-600" />;
  if (status === 'failed' || status === 'index_error' || status === 'ai_error') {
    return <XCircle className="h-3 w-3 text-red-600" />;
  }
  if (status === 'batched' || status === 'ready_for_index') {
    return <Loader2 className="h-3 w-3 animate-spin text-blue-600" />;
  }
  return <Hash className="h-3 w-3 text-muted-foreground" />;
}

function durationMs(start: number | null | undefined, end: number | null | undefined): string {
  if (!start) return '—';
  const startMs = start;
  const endMs = end ?? Date.now();
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secRem = seconds % 60;
  return `${minutes}m ${secRem}s`;
}

export default function BatchDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useBatchJob(id ?? null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading batch…
      </div>
    );
  }
  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <AlertCircle className="h-5 w-5 inline mr-2" />
          {error ? (error as Error).message : 'Batch not found'}
          <div className="mt-4">
            <Link to="/batches">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to batches
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { batch, documents } = data;
  const successful = documents.filter((d: any) => d.status === 'indexed').length;
  const failed = documents.filter(
    (d: any) => d.status === 'failed' || d.status === 'index_error' || d.status === 'ai_error',
  ).length;
  const inFlight = documents.length - successful - failed;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/batches">
            <Button variant="ghost" size="sm" className="mb-2 -ml-3">
              <ArrowLeft className="h-4 w-4 mr-1" />
              All batches
            </Button>
          </Link>
          <h2 className="text-3xl font-bold tracking-tight font-mono break-all">
            {batch.batchId}
          </h2>
          <p className="text-muted-foreground mt-1">
            <Badge variant="outline" className="mr-2">{batch.entityType}</Badge>
            <Badge variant={STATUS_VARIANT[batch.status] ?? 'secondary'}>
              {batch.status.replace('_', ' ')}
            </Badge>
            {batch.triggerReason && (
              <span className="ml-2 text-xs">trigger: {batch.triggerReason}</span>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total documents</CardDescription>
            <CardTitle className="text-3xl">{batch.documentCount ?? documents.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Indexed</CardDescription>
            <CardTitle className="text-3xl text-green-600">{successful}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In flight</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{inFlight}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed</CardDescription>
            <CardTitle className={`text-3xl ${failed > 0 ? 'text-red-600' : ''}`}>{failed}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Batch metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {batch.anthropicBatchId && (
            <div>
              <span className="text-muted-foreground">Anthropic batch ID:</span>{' '}
              <span className="font-mono">{batch.anthropicBatchId}</span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Created:</span>{' '}
            {batch.createdAt ? format(batch.createdAt, 'MMM d, yyyy h:mm:ss a') : '—'}{' '}
            {batch.createdAt && (
              <span className="text-muted-foreground">({formatDistanceToNowStrict(batch.createdAt)} ago)</span>
            )}
          </div>
          {batch.completedAt && (
            <div>
              <span className="text-muted-foreground">Completed:</span>{' '}
              {format(batch.completedAt, 'MMM d, yyyy h:mm:ss a')}
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Duration:</span>{' '}
            {durationMs(batch.createdAt, batch.completedAt)}
          </div>
          {batch.errorMessage && (
            <div className="text-red-600">
              <span className="text-muted-foreground">Error:</span> {batch.errorMessage}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Documents ({documents.length})</CardTitle>
          <CardDescription>Per-document status within this batch</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Indexed</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No documents associated with this batch yet
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((d: any) => (
                    <TableRow key={d.documentId}>
                      <TableCell className="font-mono text-xs">{d.documentId}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          {statusIcon(d.status)}
                          <Badge variant={STATUS_VARIANT[d.status] ?? 'secondary'}>
                            {d.status.replace('_', ' ')}
                          </Badge>
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{d.retryCount ?? 0}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {d.createdAt ? (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(d.createdAt, 'MMM d, h:mm a')}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {d.indexedAt ? format(d.indexedAt, 'MMM d, h:mm a') : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-red-600 max-w-md break-words">
                        {d.errorMessage ?? ''}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
